/* eslint-disable import/no-named-as-default */
import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { array, object, oneOfType, string, bool } from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import List from 'yoda-core-components/lib/components/List/List';
import renderCurrentComponent from 'yoda-site-components/lib/components/IrisPage/renderCurrentComponent';
import { isMobileWidth, mobileQuery } from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import { getURLParameterBykey } from '../../utils';
import { viewTypes } from '../../configurations/config';
import * as styles from './ProductList.css';
import GalleryProductCard from '../GalleryProductCard/GalleryProductCard';
import ProductMarketingTile from '../ProductMarketingTile/ProductMarketingTile';
import ProductsLoader from './ProductsLoader';
import ScrollRendererWrapper from '../ScrollRendererWrapper/ScrollRendererWrapper';
import ProductSkeleton from './ProductSkeleton';

const cx = classNames.bind(styles);

/* istanbul ignore next */
const ProductListRenderer = ({
    dataSource: { adMonetization, productList },
    gridAds,
    criteoComponent,
    irisComponents,
    irisComponentsWithoutGrid,
    irisPropsData,
    marketingSlot,
    enableVerticalImages,
    enableVerticalImageLg,
}) => {
    const state = useSelector((reduxState) => reduxState);
    const {
        context: {
            deviceType: { isMobile = false, isTablet = false },
            featureFlags: { enableFourThumbnailsGrid = false, enableConstructorIO = true },
        } = {},
        productSettings: { isFacetOpened = false, viewType = 'grid' } = {},
        pagination: { pagination: { selectedPageNumber: pageNumber = 0 } = {} } = {},
        countOfSearchResults = 0,
        seoTitleTags: { searchTerm = '' } = {},
        enableLoader = false,
    } = state;

    const [custStyle4thumbnail, setCustStyle4thumbnail] = useState('');

    const handleResize = (ismobile) => {
        if (ismobile) {
            setCustStyle4thumbnail('w-1/2');
        } else if (
            (!ismobile && isFacetOpened && enableFourThumbnailsGrid) ||
            enableVerticalImages
        ) {
            setCustStyle4thumbnail('w-1/4');
        } else if (!ismobile && isFacetOpened) {
            setCustStyle4thumbnail('w-1/3');
        }
    };

    const handleVerticalImageResize = (e) => {
        const orientation =
            e?.target?.type ||
            e?.target?.screen?.orientation?.type ||
            screen?.orientation?.type ||
            (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
        const showThreeCol =
            enableVerticalImages &&
            !__SERVER__ &&
            window?.innerWidth < 749 &&
            orientation.toLowerCase().includes('landscape');
        const showFourCol =
            enableVerticalImages &&
            !__SERVER__ &&
            window?.innerWidth >= 749 &&
            orientation.toLowerCase().includes('landscape');

        if (orientation.toLowerCase().includes('portrait')) {
            setCustStyle4thumbnail('w-1/2');
        } else if (showThreeCol) {
            setCustStyle4thumbnail('w-1/3');
        } else if (showFourCol) {
            setCustStyle4thumbnail('w-1/4');
        }
    };

    useEffect(() => {
        handleResize(isMobileWidth());
        !__SERVER__ && enableVerticalImages && handleVerticalImageResize();

        const orientationSupport = !__SERVER__ && screen?.orientation;
        if (!__SERVER__ && orientationSupport) {
            screen?.orientation?.addEventListener('change', handleVerticalImageResize);
        } else {
            window.addEventListener('orientationchange', handleVerticalImageResize);
        }
        const mQuery = window.matchMedia(mobileQuery);
        /* istanbul ignore next */
        mQuery.onchange = (e) => handleResize(e?.matches);
        return () => {
            if (!__SERVER__ && orientationSupport) {
                screen.orientation.removeEventListener('change', handleVerticalImageResize);
            } else {
                window.removeEventListener('orientationchange', handleVerticalImageResize);
            }
        };
    }, []);

    const renderChildComponent = (product) => {
        const {
            enableLoader = false,
            multiSelectFilter: { enableProductsLoader = false } = {},
            context: { deviceType = {} } = {},
        } = state;

        if (enableProductsLoader && enableLoader) {
            return <ProductsLoader hasProducts />;
        }

        if (product.criteoSlot) return null;

        if (product.marketingSlot && viewType !== viewTypes.List) {
            return (
                <ScrollRendererWrapper>
                    <ProductMarketingTile
                        deviceType={deviceType}
                        disableLoader
                        irisComponents={irisComponents}
                        irisComponentsWithoutGrid={irisComponentsWithoutGrid}
                        irisPropsData={irisPropsData}
                        marketingSlot={marketingSlot}
                        productMarketingSlot={product.marketingSlot}
                        slotPosition={product.marketingSlot.slotPosition}
                    />
                </ScrollRendererWrapper>
            );
        }

        return (
            <ScrollRendererWrapper
                loader={<ProductSkeleton className={dt(['sm:px-1', 'md:px-2'])} />}
            >
                <GalleryProductCard
                    cardType={viewType}
                    fromProductList
                    index={product.index}
                    productDetails={product}
                    showVerticalProductImage={enableVerticalImages}
                    enableVerticalImageLg={enableVerticalImageLg}
                />
            </ScrollRendererWrapper>
        );
    };

    const itemStyleClass = `${cx('list-item')} ${dt([
        'mb-6',
        isMobile && 'w-1/2',
        isTablet && !custStyle4thumbnail && 'w-1/3',
        custStyle4thumbnail,
        viewType === viewTypes.LIST && 'w-full',
    ])}`;

    const renderProductList = (products) => (
        <MemoizedList
            automationId="gallery-product-list"
            childRenderer={(product) => renderChildComponent(product)}
            datasource={products}
            direction="Wrap"
            itemStyleClass={itemStyleClass}
            listBodyClass={cx('list-container')}
            listStyleClass={`${dt(['flex', 'flex-wrap'])} ${cx('list')}`}
            spacing="None"
            listMainCustomClass={cx('gallery-list')}
        />
    );

    const renderSplitProductList = (products) => {
        const splitIndex = products.findIndex((p) => p.criteoSlot);
        const isFirstPage = pageNumber === 1;
        if (splitIndex === -1 || !criteoComponent || !isFirstPage)
            return renderProductList(products.filter((p) => !p.criteoSlot));
        const before = products.slice(0, splitIndex);
        const after = products.slice(splitIndex + 1);
        return (
            <>
                {before.length > 0 && renderProductList(before)}
                <div className={dt(['w-full'])}>
                    {renderCurrentComponent(
                        {
                            ...criteoComponent,
                            content: { ...criteoComponent?.content, mode: 'ingrid' },
                        },
                        0,
                        irisComponents,
                        irisComponentsWithoutGrid,
                        irisPropsData
                    )}
                </div>
                {after.length > 0 && renderProductList(after)}
            </>
        );
    };

    const getCnstrcContainerAttrs = () => {
        if (!enableConstructorIO || __SERVER__) return {};
        const currentPage = getURLParameterBykey('page') || '1';
        const searchTermFromUrl = getURLParameterBykey('searchTerm');
        const cnstrcSearchTerm = searchTermFromUrl || searchTerm;
        const isSearchPage = window.location.pathname.startsWith('/s');
        const isBrowsePage = window.location.pathname.startsWith('/g');

        if (isSearchPage) {
            return {
                'data-cnstrc-search': '',
                'data-cnstrc-search-term': cnstrcSearchTerm,
                ...(!enableLoader && { 'data-cnstrc-num-results': String(countOfSearchResults) }),
                'data-cnstrc-result-page': currentPage,
            };
        }
        if (isBrowsePage) {
            const pathSegments = window.location.pathname.split('/').filter(Boolean);
            const filterValue = pathSegments.slice(1).join('>') || '';
            return {
                'data-cnstrc-browse': '',
                'data-cnstrc-filter-name': 'group_id',
                'data-cnstrc-filter-value': filterValue,
                ...(!enableLoader && { 'data-cnstrc-num-results': String(countOfSearchResults) }),
                'data-cnstrc-result-page': currentPage,
            };
        }
        return {};
    };

    return (
        <div {...getCnstrcContainerAttrs()}>
            {productList
                ? renderSplitProductList(productList)
                : adMonetization &&
                  renderCurrentComponent(
                      gridAds,
                      0,
                      irisComponents,
                      irisComponentsWithoutGrid,
                      irisPropsData
                  )}
        </div>
    );
};

const MemoizedList = memo(List);

ProductListRenderer.defaultProps = {
    gridAds: {},
    criteoComponent: null,
    irisComponents: {},
    irisComponentsWithoutGrid: {},
    irisPropsData: {},
    marketingSlot: {},
    enableVerticalImages: false,
    enableVerticalImageLg: false,
};

ProductListRenderer.propTypes = {
    dataSource: oneOfType([array, object]).isRequired,
    gridAds: oneOfType([object, string]),
    criteoComponent: object,
    irisComponents: oneOfType([array, object]),
    irisComponentsWithoutGrid: oneOfType([array, object]),
    irisPropsData: oneOfType([array, object]),
    marketingSlot: oneOfType([array, object]),
    enableVerticalImages: bool,
    enableVerticalImageLg: bool,
};

export default ProductListRenderer;
